import { useEffect, useRef } from 'react';

import type { AppResumeReason } from './useAppResume';

export type { AppResumeReason } from './useAppResume';

/**
 * Web build of {@link useAppResume}.
 *
 * A tab has two ways to come back where an app has one. `visibilitychange` is
 * the foreground analogue — it covers a tab switch and a machine waking from
 * sleep, which is where a credential minted before the sleep has expired
 * unnoticed. `online` covers the case a visible tab cannot report any other
 * way: the network dropped and returned with nothing else about the page
 * changing.
 *
 * Both are guarded rather than assumed. Web routes are also rendered statically
 * at build time, where neither global exists.
 */
export function useAppResume(onResume: (reason: AppResumeReason) => void): void {
  const onResumeRef = useRef(onResume);

  onResumeRef.current = onResume;

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        onResumeRef.current('foreground');
      }
    };

    const handleOnline = () => onResumeRef.current('reconnect');

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('online', handleOnline);
    }

    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }

      if (typeof window !== 'undefined') {
        window.removeEventListener('online', handleOnline);
      }
    };
  }, []);
}
